import api from './axios';
import { logError, logDebug } from '../common/utils/logger';
import { stripDocumentSnapshotForApi } from '../common/utils/documentFileMeta';
import { API_ENDPOINTS } from '../constants';
import {
  decryptJourneyQueryValue,
  encryptJourneyQueryValue,
  encryptJourneySensitiveValue,
} from '../common/utils/journeyEncryption';
import { getEffectiveApplicationNumber } from '../common/utils/journeyApplicationNumber';

/** Plain app number for APIs (decrypt URL/session ciphertext; never double-encrypt). */
const toPlainApplicationNumber = (applicationNumber) =>
  getEffectiveApplicationNumber(applicationNumber);
const RESUME_DOB_SESSION_KEY = 'rnlic_resumeDob';
const getResumeDobStorageKey = (applicationNumber) =>
  applicationNumber ? `${RESUME_DOB_SESSION_KEY}:${String(applicationNumber).trim()}` : RESUME_DOB_SESSION_KEY;

export const JOURNEY_STAGE = {
  QUOTE_SUMMARY: 'Stage 4.0 QuoteSummary',
  PAYMENT_GATEWAY_REDIRECTION: 'Stage 5.0 PaymentGatewayRedirection',
  CUSTOMER_VERIFICATION_SKIP_FLOW: 'Stage 5.0 CustomerVerificationPage',
  PAYMENT_RECEIPT_DOWNLOAD: 'Stage 6.0 PaymentReceiptDownloadPage',
  CUSTOMER_VERIFICATION_PAY_FLOW: 'Stage 7.0 CustomerVerificationPage',
  PROPOSAL_SCREEN_1: 'Stage 8.0 ProposalFormScreen1',
  PROPOSAL_SCREEN_2: 'Stage 8.1 ProposalFormScreen2',
  PROPOSAL_SCREEN_3: 'Stage 8.2 ProposalFormScreen3',
  PROPOSAL_SCREEN_4: 'Stage 8.3 ProposalFormScreen4',
  PROPOSAL_SCREEN_5: 'Stage 8.4 ProposalFormScreen5',
  PROPOSAL_SCREEN_6: 'Stage 8.5 ProposalFormScreen6',
  PROPOSAL_SUMMARY_WITH_PVC: 'Stage 9.0 ProposalSummaryWithPVCNewTab',
  OTP_VERIFICATION_PAY_REDIRECTION: 'Stage 9.1 OTPVerificationPayRedirection',
  OTP_SUBMISSION_WITH_PAY_NOW: 'Stage 9.2 OTPSubmissionWithPayNow',
  THANK_YOU_PAGE_REVERSE_FEED: 'Stage 10.0 ThankYouPageReverseFeed',
};

export const PAYMENT_FLOW = {
  PAY_FIRST: 'PAY_FIRST',
  PAY_LAST: 'PAY_LAST',
};

export const getCustomerVerificationStageByFlow = (paymentFlowMode) => (
  paymentFlowMode === PAYMENT_FLOW.PAY_FIRST
    ? JOURNEY_STAGE.CUSTOMER_VERIFICATION_PAY_FLOW
    : JOURNEY_STAGE.CUSTOMER_VERIFICATION_SKIP_FLOW
);

/**
 * Proposal UI matches three top tabs; each tab has one or more sections saved as separate
 * journey cumulative keys (Stage 8.0–8.5). Use this for resume, analytics, and support.
 */
export const PROPOSAL_UI_TABS = [
  {
    id: 'lifeAssured',
    label: 'Life Assured',
    icon: 'shield',
    sections: [
      {
        stepId: 'lifeAssured-personal',
        sectionLabel: 'Personal details',
        journeyStage: JOURNEY_STAGE.PROPOSAL_SCREEN_1,
        dataField: 'lifeAssuredPersonalDetails',
        rawTextField: 'lifeAssuredPersonalDetailsRawText',
      },
      {
        stepId: 'lifeAssured-bank',
        sectionLabel: 'Bank details',
        journeyStage: JOURNEY_STAGE.PROPOSAL_SCREEN_2,
        dataField: 'lifeAssuredBankDetails',
        rawTextField: 'lifeAssuredBankDetailsRawText',
      },
      {
        stepId: 'lifeAssured-additional',
        sectionLabel: 'Additional information',
        journeyStage: JOURNEY_STAGE.PROPOSAL_SCREEN_3,
        dataField: 'lifeAssuredAdditionalInformation',
        rawTextField: 'lifeAssuredAdditionalInformationRawText',
      },
    ],
  },
  {
    id: 'nominee',
    label: 'Nominee',
    icon: 'people',
    sections: [
      {
        stepId: 'nominee',
        sectionLabel: 'Nominee details',
        journeyStage: JOURNEY_STAGE.PROPOSAL_SCREEN_4,
        dataField: 'nomineeDetails',
        rawTextField: 'nomineeDetailsRawText',
      },
    ],
  },
  {
    id: 'medical',
    label: 'Medical',
    icon: 'medical',
    sections: [
      {
        stepId: 'medical-lifestyle',
        sectionLabel: 'Lifestyle details',
        journeyStage: JOURNEY_STAGE.PROPOSAL_SCREEN_5,
        dataField: 'medicalLifestyleDetails',
        rawTextField: 'medicalLifestyleDetailsRawText',
      },
      {
        stepId: 'medical-medical',
        sectionLabel: 'Medical details',
        journeyStage: JOURNEY_STAGE.PROPOSAL_SCREEN_6,
        dataField: 'medicalDetails',
        rawTextField: 'medicalDetailsRawText',
      },
    ],
  },
];

/** Top tab bar only (id / label / icon) — same order as `Proposal.jsx` tabs */
export const PROPOSAL_TOP_TAB_BAR = PROPOSAL_UI_TABS.map(({ id, label, icon }) => ({
  id,
  label,
  icon,
}));

const readStoredResumeDob = (applicationNumber) => {
  try {
    const scopedKey = getResumeDobStorageKey(applicationNumber);
    const dob =
      sessionStorage.getItem(scopedKey) ||
      sessionStorage.getItem(RESUME_DOB_SESSION_KEY);
    return typeof dob === 'string' && dob.trim() ? dob.trim() : null;
  } catch {
    return null;
  }
};

const readDobFromLeadFormSession = () => {
  try {
    const rawLeadForm = sessionStorage.getItem('inlic_lead_form_data');
    if (!rawLeadForm) return null;
    const parsedLeadForm = JSON.parse(rawLeadForm);
    const dob = parsedLeadForm?.dateOfBirth;
    return typeof dob === 'string' && dob.trim() ? dob.trim() : null;
  } catch {
    return null;
  }
};

const resolveOwnershipDob = (applicationNumber, explicitDob) => {
  const explicit =
    typeof explicitDob === 'string' && explicitDob.trim()
      ? explicitDob.trim()
      : null;
  if (explicit) return explicit;
  return readDobFromLeadFormSession() || readStoredResumeDob(applicationNumber);
};

const storeResumeDob = (dob, applicationNumber) => {
  if (!dob || typeof dob !== 'string' || !dob.trim()) return;
  try {
    const normalized = dob.trim();
    const scopedKey = getResumeDobStorageKey(applicationNumber);
    sessionStorage.setItem(scopedKey, normalized);
    // keep legacy/global key for backward compatibility across in-flight tabs
    sessionStorage.setItem(RESUME_DOB_SESSION_KEY, normalized);
  } catch {
    // ignore session storage write issues
  }
};

const extractDobFromJourneyResponse = (journeyData) => {
  if (!journeyData || typeof journeyData !== 'object') return null;
  const stageDob =
    journeyData?.stages?.['Stage 1.0 Lead']?.dateOfBirth ||
    journeyData?.currentStageData?.['Stage 1.0 Lead']?.dateOfBirth ||
    journeyData?.dateOfBirth ||
    null;
  return typeof stageDob === 'string' && stageDob.trim() ? stageDob.trim() : null;
};

export const PROPOSAL_STAGE_BY_STEP = PROPOSAL_UI_TABS.flatMap((t) => t.sections).reduce(
  (acc, s) => {
    acc[s.stepId] = s.journeyStage;
    return acc;
  },
  {},
);

const PROPOSAL_STEP_CONFIG = PROPOSAL_UI_TABS.flatMap((t) => t.sections).reduce(
  (acc, s) => {
    acc[s.stepId] = {
      stageName: s.journeyStage,
      dataField: s.dataField,
      rawTextField: s.rawTextField,
    };
    return acc;
  },
  {},
);

/**
 * Ordered groups for the full online term journey (cumulative keys / currentStage labels).
 * Use with parsed `cummulativeJourneyData` to see which steps are present.
 */
export const TERM_JOURNEY_STAGE_SEQUENCE = [
  {
    group: 'Lead',
    description: 'Capture and eligibility',
    keys: ['Stage 1.0 Lead', 'Stage 1.1 Lead', 'Stage 1.2 Lead', 'Stage 2.0 Lead'],
  },
  { group: 'Quote', description: 'Premium / plan', keys: ['Stage 3.0 Quote'] },
  { group: 'Quote summary', description: 'Declarations & preferences', keys: [JOURNEY_STAGE.QUOTE_SUMMARY] },
  {
    group: 'Payment & verification',
    description: 'Payment redirect, receipt, customer verification (5.x / 6.x / 7.x)',
    keys: [
      JOURNEY_STAGE.PAYMENT_GATEWAY_REDIRECTION,
      JOURNEY_STAGE.PAYMENT_RECEIPT_DOWNLOAD,
      JOURNEY_STAGE.CUSTOMER_VERIFICATION_SKIP_FLOW,
      JOURNEY_STAGE.CUSTOMER_VERIFICATION_PAY_FLOW,
    ],
  },
  {
    group: 'Proposal',
    description: 'Three tabs: Life Assured (8.0–8.2), Nominee (8.3), Medical (8.4–8.5)',
    keys: [
      JOURNEY_STAGE.PROPOSAL_SCREEN_1,
      JOURNEY_STAGE.PROPOSAL_SCREEN_2,
      JOURNEY_STAGE.PROPOSAL_SCREEN_3,
      JOURNEY_STAGE.PROPOSAL_SCREEN_4,
      JOURNEY_STAGE.PROPOSAL_SCREEN_5,
      JOURNEY_STAGE.PROPOSAL_SCREEN_6,
    ],
  },
  {
    group: 'Completion',
    description: 'Proposal summary / OTP / thank you',
    keys: [
      JOURNEY_STAGE.PROPOSAL_SUMMARY_WITH_PVC,
      JOURNEY_STAGE.OTP_VERIFICATION_PAY_REDIRECTION,
      JOURNEY_STAGE.OTP_SUBMISSION_WITH_PAY_NOW,
      JOURNEY_STAGE.THANK_YOU_PAGE_REVERSE_FEED,
    ],
  },
];

const pickFields = (source = {}, fields = []) => {
  return fields.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      acc[key] = source[key];
    }
    return acc;
  }, {});
};

const FILE_META = '__fileMeta';

/**
 * Serialize File/Blob for JSON storage (journey / proposal payloads). Avoids empty {} from JSON.stringify(File).
 */
const sanitizeValueForJourney = (value) => {
  if (value == null) return value;
  if (typeof File !== 'undefined' && value instanceof File) {
    return {
      [FILE_META]: true,
      name: value.name,
      size: value.size,
      type: value.type,
      lastModified: value.lastModified,
    };
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob && !(value instanceof File)) {
    return { [FILE_META]: true, size: value.size, type: value.type };
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeValueForJourney);
  }
  if (value && typeof value === 'object' && value.constructor === Object) {
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = sanitizeValueForJourney(value[k]);
    });
    return out;
  }
  return value;
};

const sanitizeProposalPayload = (obj) => sanitizeValueForJourney(obj);

const buildNomineePayload = (stepInputData = {}) => {
  const rawNominees = Array.isArray(stepInputData?.nominees) ? stepInputData.nominees : [];
  return {
    activeNominee: stepInputData?.activeNominee ?? 0,
    nominees: sanitizeProposalPayload(rawNominees),
  };
};

const buildStepDataPayload = (stepId, stepInputData = {}) => {
  const meta = pickFields(stepInputData || {}, [
    'tab',
    'stepId',
    'proposalDocumentsSnapshot',
  ]);
  if (meta.proposalDocumentsSnapshot != null) {
    meta.proposalDocumentsSnapshot = stripDocumentSnapshotForApi(
      meta.proposalDocumentsSnapshot,
    );
  }

  if (stepId === 'nominee') {
    return sanitizeProposalPayload({
      ...buildNomineePayload(stepInputData),
      ...meta,
    });
  }

  const rawFd = stepInputData?.formData;
  const fd =
    rawFd && typeof rawFd === 'object' ? sanitizeProposalPayload(rawFd) : {};

  if (stepId === 'medical-medical') {
    const declarationConsents = stepInputData?.declarationConsents;
    const normalizedDeclarationConsents =
      declarationConsents && typeof declarationConsents === 'object'
        ? sanitizeProposalPayload(declarationConsents)
        : null;
    const declarationConsentValues = Array.isArray(declarationConsents)
      ? declarationConsents
      : normalizedDeclarationConsents
        ? Object.values(normalizedDeclarationConsents)
        : [];
    const declarationConsentsAccepted =
      declarationConsentValues.length > 0 &&
      declarationConsentValues.every((value) =>
        typeof value === 'string'
          ? value.trim().toLowerCase() === 'true'
          : Boolean(value),
      );

    return sanitizeProposalPayload({
      ...fd,
      ...meta,
      ...((Array.isArray(declarationConsents) || normalizedDeclarationConsents)
        ? {
            declarationConsents: Array.isArray(declarationConsents)
              ? declarationConsents
              : normalizedDeclarationConsents,
            declarationConsentsAccepted,
          }
        : {}),
    });
  }

  return sanitizeProposalPayload({ ...fd, ...meta });
};

/**
 * Update journey stage by calling the backend journey update API.
 * This updates both the Case and Journey tables with the new stage.
 * Uses the same endpoint as lead submission but with stage parameter.
 * @param {string} applicationNumber - Application number (required)
 * @param {string} caseId - Case ID (required)
 * @param {string} stage - The stage to update to (e.g., 'Summary Stage', 'Payment Stage')
 * @param {Object} additionalData - Additional data to store with the journey update
 * @returns {Promise<Object>} Journey update response
 */
export const updateJourneyStage = async (applicationNumber, caseId, stage, additionalData = {}) => {
  try {
    const plainAppNumber = toPlainApplicationNumber(applicationNumber);
    if (!plainAppNumber) {
      throw new Error('Application number is required to update journey stage.');
    }
    if (!caseId) {
      throw new Error('Case ID is required to update journey stage.');
    }
    if (!stage) {
      throw new Error('Stage is required to update journey.');
    }

    // Spread additionalData first so the explicit `stage` argument always wins over any
    // nested currentStage (e.g. verification snapshots) and matches backend expectations.
    const effectiveDob = resolveOwnershipDob(plainAppNumber);

    const requestPayload = {
      ...additionalData,
      applicationNumber: encryptJourneyQueryValue(plainAppNumber),
      caseId,
      ...(effectiveDob ? { dateOfBirth: encryptJourneyQueryValue(effectiveDob) } : {}),
      currentStage: stage,
    };

    logDebug('[updateJourneyStage] Updating journey to stage:', stage, requestPayload);

    // Use the submitLead endpoint which handles all stage transitions
    const response = await api.post(API_ENDPOINTS.SUBMIT_LEAD, requestPayload);
    
    logDebug('[updateJourneyStage] Journey updated successfully:', response?.data);
    
    return response?.data ?? null;
  } catch (error) {
    logError('[updateJourneyStage] Failed to update journey stage', error);
    throw error;
  }
};

/**
 * Save proposal step snapshot into ProposalData object table.
 * Uses /termJourney/proposalData and maps each step to proposal 4.x stage names.
 * @param {string} applicationNumber - Application number (required)
 * @param {string} stepId - Proposal step key (required)
 * @param {Object} stepInputData - Full step input snapshot
 * @returns {Promise<Object>} API response
 */
export const saveProposalStepData = async (applicationNumber, stepId, stepInputData = {}, caseId = null) => {
  try {
    const plainAppNumber = toPlainApplicationNumber(applicationNumber);
    if (!plainAppNumber) {
      throw new Error('Application number is required to save proposal data.');
    }
    if (!stepId) {
      throw new Error('Step ID is required to save proposal data.');
    }

    const stepConfig = PROPOSAL_STEP_CONFIG[stepId];
    if (!stepConfig) {
      throw new Error(`Unsupported proposal step: ${stepId}`);
    }

    const stageData = buildStepDataPayload(stepId, stepInputData);

    const stageSnapshot = {
      stageName: stepConfig.stageName,
      stepId,
      savedAt: new Date().toISOString(),
      ...stageData,
    };

    const effectiveDob = resolveOwnershipDob(plainAppNumber);
    const requestPayload = {
      applicationNumber: encryptJourneyQueryValue(plainAppNumber),
      ...(effectiveDob ? { dateOfBirth: encryptJourneyQueryValue(effectiveDob) } : {}),
      [stepConfig.dataField]: JSON.stringify(stageSnapshot),
      [stepConfig.rawTextField]: JSON.stringify(stageSnapshot, null, 2),
    };

    logDebug('[saveProposalStepData] Saving proposal step data:', requestPayload);

    const response = await api.post(API_ENDPOINTS.PROPOSAL_DATA, requestPayload);

    // Keep journey table in sync with proposal stage snapshots.
    // This makes proposal data discoverable in journey current/cumulative fields as well.
    if (caseId) {
      await updateJourneyStage(plainAppNumber, caseId, stepConfig.stageName, stageData);
    }

    logDebug('[saveProposalStepData] Proposal step saved successfully:', response?.data);

    return response?.data ?? null;
  } catch (error) {
    logError('[saveProposalStepData] Failed to save proposal step data', error);
    throw error;
  }
};

/**
 * Save proposal summary for the current journey. Backend saves to proposalSummary table, then updates journey and case to Stage 9.0.
 * @param {string} applicationNumber - Application number (required)
 * @param {Object} [summaryData] - Optional: { documentsUploaded, pivcStatus, summaryStatus }
 * @returns {Promise<Object>} API response (success, id)
 */
export const saveProposalSummary = async (applicationNumber, summaryData = {}) => {
  try {
    const plainAppNumber = toPlainApplicationNumber(applicationNumber);
    if (!plainAppNumber) {
      throw new Error('Application number is required to save proposal summary.');
    }
    const effectiveDob = resolveOwnershipDob(plainAppNumber);
    const requestPayload = {
      applicationNumber: encryptJourneyQueryValue(plainAppNumber),
      ...summaryData,
      ...(effectiveDob ? { dateOfBirth: encryptJourneyQueryValue(effectiveDob) } : {}),
    };
    logDebug('[saveProposalSummary] Saving proposal summary:', requestPayload);
    const response = await api.post(API_ENDPOINTS.PROPOSAL_SUMMARY, requestPayload);
    logDebug('[saveProposalSummary] Success:', response?.data);
    return response?.data ?? null;
  } catch (error) {
    logError('[saveProposalSummary] Failed to save proposal summary', error);
    throw error;
  }
};

/**
 * Get journey data by application number
 * @param {string} applicationNumber - Application number (required)
 * @param {string} [dateOfBirth] - Date of birth (yyyy-MM-dd) for first-time ownership verification
 * @returns {Promise<Object>} Journey data
 */
export const getJourneyData = async (applicationNumber, dateOfBirth) => {
  try {
    const plainAppNumber = toPlainApplicationNumber(applicationNumber);
    if (!plainAppNumber && !dateOfBirth) {
      window.location.href = '/resume-journey';
      return null;
    }
    if (!plainAppNumber) {
      throw new Error('Application number is required to load journey data.');
    }

    const rawDob =
      (typeof dateOfBirth === 'string' && dateOfBirth.trim())
        ? dateOfBirth.trim()
        : resolveOwnershipDob(plainAppNumber);
    const effectiveDob = rawDob
      ? (decryptJourneyQueryValue(rawDob) || rawDob)
      : null;

    const payload = {
      applicationNumber: encryptJourneyQueryValue(plainAppNumber),
    };
    if (effectiveDob) {
      payload.dateOfBirth = encryptJourneySensitiveValue(effectiveDob);
    }
    const response = await api.post(API_ENDPOINTS.GET_JOURNEY, payload);
    const data = response?.data ?? null;

    const dobFromResponse = extractDobFromJourneyResponse(data);
    if (dobFromResponse && plainAppNumber) {
      storeResumeDob(dobFromResponse, plainAppNumber);
    } else if (effectiveDob && plainAppNumber) {
      storeResumeDob(effectiveDob, plainAppNumber);
    }

    return data;
  } catch (error) {
    logError('[getJourneyData] Failed to get journey data', error);
    throw error;
  }
};

/**
 * Save summary stage data (declarations, preferences) to journey
 * @param {string} applicationNumber - Application number (required)
 * @param {string} caseId - Case ID (required)
 * @param {Object} summaryData - Summary stage data including declarations and preferences
 * @returns {Promise<Object>} API response
 */
export const saveSummaryStageData = async (applicationNumber, caseId, summaryData) => {
  try {
    const plainAppNumber = toPlainApplicationNumber(applicationNumber);
    if (!plainAppNumber || !caseId) {
      throw new Error('Application number and Case ID are required.');
    }

    const effectiveDob = resolveOwnershipDob(plainAppNumber);
    const requestPayload = {
      applicationNumber: encryptJourneyQueryValue(plainAppNumber),
      caseId,
      ...(effectiveDob ? { dateOfBirth: encryptJourneyQueryValue(effectiveDob) } : {}),
      currentStage: JOURNEY_STAGE.QUOTE_SUMMARY,
      summaryData: {
        benefitDeclarationAccepted: summaryData.benefitDeclaration || false,
        termsAgreementAccepted: summaryData.termsAgreement || false,
        autoRenewalEnabled: summaryData.autoRenewal || false,
        timestamp: new Date().toISOString(),
        ...summaryData,
      },
    };

    logDebug('[saveSummaryStageData] Saving summary stage data (Stage 4):', requestPayload);

    // Use the submitLead endpoint which handles all stage transitions
    const response = await api.post(API_ENDPOINTS.SUBMIT_LEAD, requestPayload);
    
    logDebug('[saveSummaryStageData] Summary stage data saved:', response?.data);
    
    return response?.data ?? null;
  } catch (error) {
    logError('[saveSummaryStageData] Failed to save summary stage data', error);
    throw error;
  }
};

/**
 * Verify PAN details and persist customer verification stage data.
 * Any upstream verification failures (e.g. NSDL) are surfaced via API errors.
 * @param {Object} verificationData - Verification payload from customer verification step
 * @returns {Promise<Object>} API response
 */
/**
 * Save journey completion (thank-you page): saves reverse feed to journeycompletions table,
 * updates journey to Stage 10.0, and closes the case.
 * @param {string} applicationNumber - Application number (required)
 * @param {Object} [payload] - { proposalFormStatus, reverseFeedStatus }
 * @returns {Promise<Object>} API response
 */
export const saveJourneyCompletion = async (applicationNumber, payload = {}) => {
  try {
    const plainAppNumber = toPlainApplicationNumber(applicationNumber);
    if (!plainAppNumber) {
      throw new Error('Application number is required to save journey completion.');
    }
    const effectiveDob = resolveOwnershipDob(plainAppNumber);
    const requestPayload = {
      applicationNumber: encryptJourneyQueryValue(plainAppNumber),
      proposalFormStatus: payload.proposalFormStatus ?? '',
      reverseFeedStatus: payload.reverseFeedStatus ?? '',
      ...(payload.otpVerificationToken ? { otpVerificationToken: payload.otpVerificationToken } : {}),
      ...(effectiveDob ? { dateOfBirth: encryptJourneyQueryValue(effectiveDob) } : {}),
    };
    logDebug('[saveJourneyCompletion] Saving journey completion:', requestPayload);
    const response = await api.post(API_ENDPOINTS.JOURNEY_COMPLETION, requestPayload);
    logDebug('[saveJourneyCompletion] Success:', response?.data);
    return response?.data ?? null;
  } catch (error) {
    logError('[saveJourneyCompletion] Failed to save journey completion', error);
    throw error;
  }
};

export const PAN_VERIFICATION_GENERIC_ERROR =
  'PAN verification failed. Please check your PAN, name, and date of birth.';

/** User-facing PAN error from panContinue API body or HTTP error payload. */
export const resolvePanVerificationMessage = (response, error) => {
  const fromBody = response?.message;
  if (typeof fromBody === 'string' && fromBody.trim()) {
    return fromBody.trim();
  }
  const fromError = error?.response?.data?.message;
  if (typeof fromError === 'string' && fromError.trim()) {
    return fromError.trim();
  }
  return PAN_VERIFICATION_GENERIC_ERROR;
};

export const verifyPanDetails = async (verificationData = {}) => {
  try {
    const payload = {
      panNumber: verificationData?.prefillData?.panNumber,
      panDateOfBirth: verificationData?.prefillData?.panDateOfBirth,
      panName: verificationData?.prefillData?.panName,
      givenName: verificationData?.dedupData?.givenName || "",
      surname: verificationData?.dedupData?.surname || "",
      sex: verificationData?.dedupData?.sex || "",
      fatherName: verificationData?.dedupData?.fatherName || "",
      spouseName: verificationData?.dedupData?.spouseName || ""
    };
    //   payload.panDateOfBirth = encryptJourneyQueryValue(payload.panDateOfBirth);
    // }
    logDebug('[verifyPanDetails] PAN Continue payload:', payload);
    const response = await api.post(
      API_ENDPOINTS.PAN_CONTINUE,
      payload
    );
    return response?.data ?? null;
  } catch (error) {
    logError('[verifyPanDetails] PAN verification failed', error);
    throw error;
  }
};

export const generateAadhaarOtp = async (aadhaarNumber) => {
  const response = await api.post(
    API_ENDPOINTS.AADHAAR_OTP_GENERATE,
    {
      id_number: aadhaarNumber
    }
  );
  return response?.data ?? null;
};

export const verifyAadhaarOtp = async (clientId, otp) => {
  const response = await api.post(
    API_ENDPOINTS.AADHAAR_OTP_VALIDATE,
    {
      client_id: clientId,
      otp: otp
    }
  );
  return response?.data ?? null;
};

export const redirectPIVCLink = async (payload) => {
  try {
    const requestPayload = { ...(payload || {}) };
    // Encrypt application number + DOB-like fields if present (backend decrypts before third-party call).
    const appKey = requestPayload.Application_Number != null ? 'Application_Number'
      : (requestPayload.applicationNumber != null ? 'applicationNumber' : null);
    if (appKey && String(requestPayload[appKey]).trim() !== '') {
      const plainApp = toPlainApplicationNumber(requestPayload[appKey]);
      if (plainApp) {
        requestPayload[appKey] = encryptJourneyQueryValue(plainApp);
      }
    }
    for (const dobKey of ['LA_DOB', 'PR_DOB', 'dateOfBirth', 'dob', 'lifeAssuredDob']) {
      if (requestPayload[dobKey] != null && String(requestPayload[dobKey]).trim() !== '') {
        requestPayload[dobKey] = encryptJourneyQueryValue(requestPayload[dobKey]);
      }
    }

    const response = await api.post(API_ENDPOINTS.PIVC_LINK, requestPayload);
    return response?.data ?? null;
  } catch (error) {
    logError('[redirectPIVCLink] Failed to create PIVC Link', error);
    throw error;
  }
};